import React from 'react';
import { compareAsc } from 'date-fns';
import { useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';

import { useSelector } from 'hooks';
import AdviceStatus from 'types/AdviceStatus';
import type { Advice } from 'types/Checklist';
import FormKeys from 'modules/form/types/FormKeys';
import { formatUndefined } from 'utils/formUtils';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { useGetSelfServiceByIdQuery } from 'modules/selfServices/service';
import useRepairOrder from 'components/RepairOrder/useRepairOrder';

const useAcceptedAdvices = (name: string) => {
  const { value: advices = [], update } = useFieldArray<Advice>(name, {
    subscription: { value: true }, format: formatUndefined,
  }).fields;

  const acceptedAdvices = React.useMemo(() => {
    const now = Date.now();
    // Already answered advices go top
    return advices
      .filter((advice) => advice.status === AdviceStatus.ACCEPTED)
      .toSorted((a, b) => compareAsc(a.answeredDate ?? now, b.answeredDate ?? now));
  }, [advices]);

  return React.useMemo(() => ({ advices, update, acceptedAdvices }), [advices, update, acceptedAdvices]);
};

const useBasketItems = () => {
  const form = useForm();

  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const { data: selfService } = useGetSelfServiceByIdQuery({ id: selfServiceId });
  const { sortedRepairOrderItems: repairOrderItems } = useRepairOrder(selfService);

  const extras = useAcceptedAdvices(`${FormKeys.EXTRAS}.extras`);
  const proactiveWorks = useAcceptedAdvices(`${FormKeys.REPAIR_ORDER}.proactiveWorks`);

  const nbWorks = repairOrderItems.length + extras.acceptedAdvices.length + proactiveWorks.acceptedAdvices.length;

  const refuseAdvice = React.useCallback(
    ({ advices, update }: ReturnType<typeof useAcceptedAdvices>, adviceId: string) => {
      const index = advices.findIndex((advice) => advice.adviceId === adviceId);

      form.batch(() => {
        update(index, { ...advices[index], status: AdviceStatus.REFUSED });

        // The consents are reset when the last work of the basket is removed
        if (nbWorks === 1) {
          form.change(`${FormKeys.POLICIES}.basketGTC`, false);
          form.change(`${FormKeys.POLICIES}.basketConsent`, false);
        }
      });
    },
    [form, nbWorks],
  );

  const onRemoveExtra = React.useCallback(
    (adviceId: string) => () => refuseAdvice(extras, adviceId),
    [refuseAdvice, extras],
  );

  const onRemoveProactiveWork = React.useCallback(
    (adviceId: string) => () => refuseAdvice(proactiveWorks, adviceId),
    [refuseAdvice, proactiveWorks],
  );

  return {
    repairOrderItems,
    acceptedExtras: extras.acceptedAdvices,
    acceptedProactiveWorks: proactiveWorks.acceptedAdvices,
    hasData: nbWorks > 0,
    onRemoveExtra,
    onRemoveProactiveWork,
  };
};

export default useBasketItems;
